package ai.wagemix.net.models

import ai.wagemix.net.models.firebase.WMSubscriptionType
import com.fsryan.foundation.models.toEnumValue
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Instant

/*
 * Billing goes through the Wagemix API (the `/api/v1/billing` routes), which talks to Stripe. These are the
 * wire shapes of those routes plus the in-memory subscription summary mirrored from the user document.
 */

/** A Stripe price for one plan (`GET /api/v1/billing/prices`). */
interface WMPlanPrice {
    val plan: WMSubscriptionType
    /** The Stripe lookup key the checkout session is created from. */
    val lookupKey: String
    /** In the currency's minor unit (cents). */
    val unitAmount: Long
    val currency: String
    /** "year" or "month". */
    val interval: String

    companion object
}

fun WMPlanPrice.Companion.kxs(plan: WMSubscriptionType, lookupKey: String, unitAmount: Long, currency: String, interval: String): KxsWMPlanPrice {
    return KxsWMPlanPrice(planValue = plan.stringValue, lookupKey = lookupKey, unitAmount = unitAmount, currency = currency, interval = interval)
}

@Serializable
@SerialName("WMPlanPrice")
@ConsistentCopyVisibility
data class KxsWMPlanPrice internal constructor(
    @SerialName("plan") val planValue: String,
    @SerialName("lookup_key") override val lookupKey: String,
    @SerialName("unit_amount") override val unitAmount: Long,
    override val currency: String,
    override val interval: String
): WMPlanPrice {
    override val plan: WMSubscriptionType get() = planValue.toEnumValue(WMSubscriptionType.None)
}

/** The body of `POST /api/v1/billing/checkout-sessions`. */
interface WMCheckoutSessionCreate {
    val lookupKey: String

    companion object
}

fun WMCheckoutSessionCreate.Companion.kxs(lookupKey: String): KxsWMCheckoutSessionCreate = KxsWMCheckoutSessionCreate(lookupKey = lookupKey)

fun WMCheckoutSessionCreate.toKxs(): KxsWMCheckoutSessionCreate = when (this) {
    is KxsWMCheckoutSessionCreate -> this
    else -> WMCheckoutSessionCreate.kxs(lookupKey = lookupKey)
}

@Serializable
@SerialName("WMCheckoutSessionCreate")
@ConsistentCopyVisibility
data class KxsWMCheckoutSessionCreate internal constructor(@SerialName("lookup_key") override val lookupKey: String): WMCheckoutSessionCreate

/** The body of `POST /api/v1/billing/portal-sessions`; [returnPath] is where the portal sends the user back. */
interface WMBillingPortalSessionCreate {
    val returnPath: String?

    companion object
}

fun WMBillingPortalSessionCreate.Companion.kxs(returnPath: String?): KxsWMBillingPortalSessionCreate {
    return KxsWMBillingPortalSessionCreate(returnPath = returnPath)
}

fun WMBillingPortalSessionCreate.toKxs(): KxsWMBillingPortalSessionCreate = when (this) {
    is KxsWMBillingPortalSessionCreate -> this
    else -> WMBillingPortalSessionCreate.kxs(returnPath = returnPath)
}

@Serializable
@SerialName("WMBillingPortalSessionCreate")
@ConsistentCopyVisibility
data class KxsWMBillingPortalSessionCreate internal constructor(
    @SerialName("return_path") override val returnPath: String? = null
): WMBillingPortalSessionCreate

/** A URL the client must open (Stripe Checkout, the billing portal). */
interface WMRedirectUrl {
    val url: String

    companion object
}

fun WMRedirectUrl.Companion.kxs(url: String): KxsWMRedirectUrl = KxsWMRedirectUrl(url = url)

@Serializable
@SerialName("WMRedirectUrl")
@ConsistentCopyVisibility
data class KxsWMRedirectUrl internal constructor(override val url: String): WMRedirectUrl

/** What the Payment Result page shows about a completed checkout (`GET /api/v1/billing/checkout-sessions/{id}`). */
interface WMCheckoutSummary {
    val plan: WMSubscriptionType
    val interval: String
    val currentPeriodEnd: Instant?

    companion object
}

fun WMCheckoutSummary.Companion.kxs(plan: WMSubscriptionType, interval: String, currentPeriodEnd: Instant?): KxsWMCheckoutSummary {
    return KxsWMCheckoutSummary(planValue = plan.stringValue, interval = interval, currentPeriodEndEpochSeconds = currentPeriodEnd?.epochSeconds)
}

@Serializable
@SerialName("WMCheckoutSummary")
@ConsistentCopyVisibility
data class KxsWMCheckoutSummary internal constructor(
    @SerialName("plan") val planValue: String,
    override val interval: String,
    @SerialName("current_period_end") val currentPeriodEndEpochSeconds: Long? = null
): WMCheckoutSummary {
    override val plan: WMSubscriptionType get() = planValue.toEnumValue(WMSubscriptionType.None)
    override val currentPeriodEnd: Instant? get() = currentPeriodEndEpochSeconds?.let { Instant.fromEpochSeconds(it) }
}

/** The subscription summary the Account page shows; the Stripe webhooks copy it onto the user document. */
interface WMSubscriptionInfo {
    val plan: WMSubscriptionType
    val interval: String?
    val currentPeriodEnd: Instant?
    val cancelAtPeriodEnd: Boolean

    companion object
}

fun WMSubscriptionInfo.Companion.of(
    plan: WMSubscriptionType,
    interval: String?,
    currentPeriodEnd: Instant?,
    cancelAtPeriodEnd: Boolean
): WMSubscriptionInfo = WMSubscriptionInfoData(plan, interval, currentPeriodEnd, cancelAtPeriodEnd)

private data class WMSubscriptionInfoData(
    override val plan: WMSubscriptionType,
    override val interval: String?,
    override val currentPeriodEnd: Instant?,
    override val cancelAtPeriodEnd: Boolean
): WMSubscriptionInfo
